import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface HumidityFogProps {
  humidity: number;
}

// Custom shader for volumetric fog
const fogVertexShader = `
  varying vec2 vUv;
  varying vec3 vPosition;
  
  void main() {
    vUv = uv;
    vPosition = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fogFragmentShader = `
  uniform float time;
  uniform float humidity;
  uniform vec3 fogColor;
  uniform float fogDensity;
  
  varying vec2 vUv;
  varying vec3 vPosition;
  
  // Simplex noise function
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
  
  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    
    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    
    i = mod289(i);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
      
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;
    
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }
  
  void main() {
    // Create volumetric effect using noise
    float noise = snoise(vPosition * 0.2 + time * 0.1);
    noise = noise * 0.5 + 0.5; // Normalize to 0-1
    
    // Calculate distance from center for falloff
    vec3 absPos = abs(vPosition);
    float maxDist = max(max(absPos.x, absPos.y), absPos.z);
    float falloff = 1.0 - smoothstep(2.0, 2.1, maxDist); // Sharp falloff at cube edges
    
    // Calculate view-dependent density
    float viewFactor = 1.0 - abs(normalize(vPosition).y) * 0.5;
    
    // Humidity-based density scaling with adjusted thresholds
    float humidityScale = 0.0;
    if (humidity < 40.0) {
      // Dry conditions - minimal fog (like old 20%)
      humidityScale = 0.1;
    } else if (humidity < 60.0) {
      // Normal conditions - moderate fog (like old 40%)
      humidityScale = 0.4;
    } else if (humidity < 80.0) {
      // Humid conditions - significant fog (like old 60%)
      humidityScale = 0.7;
    } else {
      // Very humid conditions - maximum fog (like old 80%)
      humidityScale = 1.0;
    }
    
    float density = fogDensity * viewFactor * falloff * humidityScale;
    
    // Calculate final color with view-dependent opacity
    vec3 finalColor = fogColor;
    float opacity = density * (0.5 + 0.5 * viewFactor) * falloff;
    
    gl_FragColor = vec4(finalColor, opacity);
  }
`;

// Water droplet shader
const dropletVertexShader = `
  varying vec2 vUv;
  varying float vAlpha;
  
  uniform float time;
  uniform float humidity;
  uniform float size;
  
  void main() {
    vUv = uv;
    // Fade in/out based on humidity
    vAlpha = smoothstep(0.0, 0.3, humidity) * (1.0 - smoothstep(0.7, 1.0, humidity));
    
    // Gentle floating motion
    vec3 pos = position;
    pos.y += sin(time * 0.5 + position.x) * 0.1;
    pos.x += cos(time * 0.3 + position.z) * 0.1;
    
    // Scale based on humidity
    float scale = mix(0.5, 1.0, humidity) * size;
    pos *= scale;
    
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    gl_PointSize = 2.0 * scale;
  }
`;

const dropletFragmentShader = `
  varying vec2 vUv;
  varying float vAlpha;
  
  void main() {
    // Create a circular droplet
    vec2 center = vUv - 0.5;
    float dist = length(center);
    float alpha = smoothstep(0.5, 0.0, dist) * vAlpha;
    
    // Add a highlight
    float highlight = smoothstep(0.5, 0.0, length(center - vec2(-0.2, 0.2)));
    
    gl_FragColor = vec4(1.0, 1.0, 1.0, alpha * 0.8);
    gl_FragColor.rgb += highlight * 0.3;
  }
`;

export function HumidityFog({ humidity }: HumidityFogProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const dropletsRef = useRef<THREE.Points>(null);
  const time = useRef(0);

  // Create fog material with custom shaders
  const fogMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        humidity: { value: humidity }, // Pass raw humidity value for shader thresholds
        fogColor: { value: new THREE.Color(0xffffff) },
        fogDensity: { value: 0.2 } // Base density, scaled by humidity in shader
      },
      vertexShader: fogVertexShader,
      fragmentShader: fogFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });
  }, [humidity]);

  // Create a cube geometry for the fog
  const fogGeometry = useMemo(() => {
    const geometry = new THREE.BoxGeometry(4, 4, 4); // 4x4x4 cube
    geometry.computeBoundingBox();
    const center = new THREE.Vector3();
    geometry.boundingBox?.getCenter(center);
    geometry.translate(-center.x, -center.y, -center.z);
    return geometry;
  }, []);

  // Create droplet geometry and material
  const { dropletGeometry, dropletMaterial } = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(1000 * 3);
    const sizes = new Float32Array(1000);

    // Calculate number of droplets based on humidity with adjusted thresholds
    let numDroplets = 0;
    if (humidity < 40) {
      numDroplets = 100; // Minimal droplets for dry conditions (like old 20%)
    } else if (humidity < 60) {
      numDroplets = 400; // Moderate droplets for normal conditions (like old 40%)
    } else if (humidity < 80) {
      numDroplets = 700; // More droplets for humid conditions (like old 60%)
    } else {
      numDroplets = 1000; // Full droplets for very humid conditions (like old 80%)
    }

    for (let i = 0; i < numDroplets; i++) {
      const i3 = i * 3;
      // Create a cubic distribution of droplets
      positions[i3] = (Math.random() - 0.5) * 3.8;
      positions[i3 + 1] = (Math.random() - 0.5) * 3.8;
      positions[i3 + 2] = (Math.random() - 0.5) * 3.8;
      
      // Adjust droplet size based on humidity with adjusted thresholds
      let size = 0.2;
      if (humidity < 40) {
        size = Math.random() * 0.1 + 0.1; // Smaller droplets for dry conditions (like old 20%)
      } else if (humidity < 60) {
        size = Math.random() * 0.2 + 0.15; // Medium droplets for normal conditions (like old 40%)
      } else if (humidity < 80) {
        size = Math.random() * 0.25 + 0.2; // Larger droplets for humid conditions (like old 60%)
      } else {
        size = Math.random() * 0.3 + 0.25; // Largest droplets for very humid conditions (like old 80%)
      }
      sizes[i] = size;
    }

    // Fill remaining positions with zero-sized droplets
    for (let i = numDroplets; i < 1000; i++) {
      const i3 = i * 3;
      positions[i3] = 0;
      positions[i3 + 1] = 0;
      positions[i3 + 2] = 0;
      sizes[i] = 0;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        humidity: { value: humidity / 100 },
        size: { value: 0.8 }
      },
      vertexShader: dropletVertexShader,
      fragmentShader: dropletFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    return { dropletGeometry: geometry, dropletMaterial: material };
  }, [humidity]);

  // Update time uniform for animation
  useFrame(() => {
    if (meshRef.current) {
      time.current += 0.005;
      (meshRef.current.material as THREE.ShaderMaterial).uniforms.time.value = time.current;
    }
    if (dropletsRef.current) {
      (dropletsRef.current.material as THREE.ShaderMaterial).uniforms.time.value = time.current;
    }
  });

  return (
    <>
      <mesh
        ref={meshRef}
        position={[0, 0, 0]}
        geometry={fogGeometry}
        material={fogMaterial}
      />
      <points
        ref={dropletsRef}
        position={[0, 0, 0]}
        geometry={dropletGeometry}
        material={dropletMaterial}
      />
    </>
  );
} 